import React, { useEffect, useState, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { axios, context as AuthContext } from '../../context/auth'
import { context as RehearsalContext } from '../../context/rehearsal'

import { canEdit as canEditUtil, isOwner as isOwnerUtil } from '../../utils/auth'
import PermissionsModal from '../perms/permissions-modal'
import TitleGroup from '../../styled/title-group'
import RehearsalControls from '../rehearse/controls'
import GroupWebhookSettings from './webhook-settings'

import { pageView, PAGES } from '../../utils/analytics-tracker'

const ViewGroup = () => {
  const navigate = useNavigate()
  const { groupid } = useParams()

  const { user, loading: authLoading } = useContext(AuthContext)
  const { activeRehearsals, fetchActiveRehearsals, rehearsal, isOwner, isFollowing } = useContext(RehearsalContext)

  // Redirect to login if not authenticated (but wait for auth to finish loading)
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login')
    }
  }, [user, authLoading, navigate])

  const [group, setGroup] = useState()
  const [canEdit, setCanEdit] = useState(false)
  const [activeRehearsal, setActiveRehearsal] = useState(null)
  const [loadingRehearsal, setLoadingRehearsal] = useState(false)
  const [rehearsalError, setRehearsalError] = useState(null)
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)

  useEffect(() => {
    const loadGroup = async () => {
      try {
        const response = await axios.get(`/api/groups/${groupid}`)
        setGroup(response.data)
        setCanEdit(canEditUtil(response.data))
      } catch (err) {
        console.error('Error loading group:', err)
      }
    }
    // Only fetch group after auth is complete and user is available
    if (!authLoading && user) {
      pageView(PAGES.GROUP_VIEW)
      loadGroup()
    }
  }, [groupid, authLoading, user])

  useEffect(() => {
    const loadActiveRehearsals = async () => {
      try {
        setLoadingRehearsal(true)
        setRehearsalError(null)
        await fetchActiveRehearsals()
      } catch (err) {
        console.error('Error fetching active rehearsals:', err)
        setRehearsalError('Failed to load rehearsal information')
      } finally {
        setLoadingRehearsal(false)
      }
    }

    if (groupid) {
      loadActiveRehearsals()
    }
  }, [groupid, fetchActiveRehearsals])

  useEffect(() => {
    // Find active rehearsal for this specific group
    const groupRehearsal = activeRehearsals.find(r => r.group === groupid || r.group?._id === groupid)
    setActiveRehearsal(groupRehearsal || null)
  }, [activeRehearsals, groupid])

  const clickEdit = () => {
    navigate(`/groups/${groupid}/edit`)
  }

  const handleWebhookUpdate = (updatedGroup) => {
    setGroup(updatedGroup)
  }

  if (!group) {
    return <div>loading</div>
  }

  const isGroupOwner = isOwnerUtil(group)

  // Show loading while auth is in progress
  if (authLoading) {
    return <div>Loading...</div>
  }

  return (
    <div className='ViewGroup'>
      <TitleGroup
        title={group.title}
        onEdit={canEdit && clickEdit}
        onPermissions={() => setShowPermissionsModal(true)}
      />

      {rehearsalError && (
        <div style={{ color: 'red', padding: '10px', marginBottom: '10px' }}>
          {rehearsalError}
        </div>
      )}

      {!loadingRehearsal && (
        <RehearsalControls
          groupId={groupid}
          activeRehearsal={activeRehearsal}
        />
      )}

      {(isOwner || isFollowing) && rehearsal && (
        <div style={{ padding: '10px', marginBottom: '10px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
          <strong>Your Rehearsal Status:</strong> {isOwner ? 'Leading' : 'Following'}
        </div>
      )}

      {isGroupOwner && (
        <GroupWebhookSettings group={group} onUpdate={handleWebhookUpdate} />
      )}

      {/* Permissions Modal */}
      <PermissionsModal
        isOpen={showPermissionsModal}
        onClose={() => setShowPermissionsModal(false)}
        contentType='group'
        contentId={groupid}
        contentName={group.title}
      />
    </div>
  )
}

export default ViewGroup
